/**
 * SSE (Server-Sent Events) service for real-time order updates
 */

import { refreshJwtToken } from './api';

// Use REACT_APP_SSE_URL if provided, otherwise fall back to REACT_APP_API_URL
const SSE_BASE_URL = process.env.REACT_APP_SSE_URL || process.env.REACT_APP_API_URL || 'http://localhost:8000';

const MAX_RECONNECT_DELAY_MS = 30000; // cap backoff at 30s for long-lived retries

class SSEService {
  constructor() {
    this.reader = null;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 50; // effectively long-lived retry (e.g. after API restart)
    this.reconnectDelay = 2000; // base 2 seconds
    this.reconnectTimeoutId = null;
    this.isConnecting = false;
    this.isConnected = false;
    /** Latest callback passed to connect() - used by reconnects so they get current handler */
    this.currentOnEvent = null;
    this.visibilityHandler = null;
  }

  /**
   * Get short-lived JWT token from localStorage
   * 
   * This returns the JWT token obtained from /api/v1/auth/device endpoint,
   * NOT the initial long-lived device token. The JWT token is short-lived
   * (expires in DEVICE_JWT_ACCESS_TOKEN_EXPIRE_MINUTES) and is used for
   * all authenticated API requests including SSE connections.
   */
  getAuthToken() {
    const token = localStorage.getItem('access_token'); // Short-lived JWT token
    if (token) {
      // Validate token format (basic check - should be a JWT with 3 parts)
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.error('[SSE] Invalid JWT token format - SSE connection will be skipped');
        // Не удаляем токены - пусть AuthContext или API сервис разбираются
        return null;
      }
    }
    return token;
  }

  /**
   * Connect to SSE stream
   * REQUIRES: Valid JWT token in localStorage (access_token)
   * The token will be sent in Authorization header: Bearer <token>
   */
  connect(onEvent) {
    this.currentOnEvent = onEvent != null ? onEvent : this.currentOnEvent;

    if (this.isConnecting || this.isConnected) {
      if (this.isConnected) {
        console.log('[SSE] Already connected - callback updated');
      } else {
        console.log('[SSE] Already connecting');
      }
      return;
    }

    const token = this.getAuthToken();
    if (!token) {
      console.warn('[SSE] No authentication token found. SSE connection skipped.');
      console.warn('[SSE] SSE will connect automatically after successful authentication.');
      return;
    }

    this.startVisibilityReconnect();
    console.log('[SSE] Connecting with short-lived device JWT token...');
    this.isConnecting = true;
    const url = `${SSE_BASE_URL}/api/v1/orders/device/events/stream`;
    this.connectWithFetch(url, token, this.currentOnEvent);
  }

  /**
   * Connect using fetch API to support custom headers
   */
  async connectWithFetch(url, token, onEvent) {
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/event-stream',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.warn('[SSE] JWT Authentication failed (401) - attempting token refresh...');
          this.isConnecting = false;
          this.isConnected = false;
          const newToken = await refreshJwtToken();
          if (newToken) {
            console.log('[SSE] Token refreshed, reconnecting shortly...');
            this.reconnectAttempts = 0;
            setTimeout(() => this.connect(this.currentOnEvent), 500);
          } else {
            console.error('[SSE] Token refresh failed - will retry reconnect with backoff');
            this.handleDisconnect();
          }
          return;
        }
        if (response.status === 403) {
          console.error('[SSE] Access forbidden (403) - device may not have access');
          this.isConnecting = false;
          this.isConnected = false;
          this.handleDisconnect();
          return;
        }
        throw new Error(`SSE connection failed: ${response.status} ${response.statusText}`);
      }

      this.reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      this.isConnecting = false;
      this.isConnected = true;
      const wasReconnect = this.reconnectAttempts > 0;
      this.reconnectAttempts = 0;
      this._emitConnectionStatus(wasReconnect ? 'reconnected' : 'connected');
      console.log('[SSE] Successfully connected to order events stream with device JWT authentication');

      // Process stream
      while (true) {
        const { done, value } = await this.reader.read();
        
        if (done) {
          console.log('[SSE] Stream ended');
          this.isConnected = false;
          this.reader = null;
          this.handleDisconnect();
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        let eventType = 'message';
        let eventData = '';

        for (const line of lines) {
          if (line.startsWith('event:')) {
            eventType = line.substring(6).trim();
          } else if (line.startsWith('data:')) {
            eventData = line.substring(5).trim();
          } else if (line === '') {
            // Empty line indicates end of event
            if (eventData) {
              try {
                const data = JSON.parse(eventData);
                this.handleEvent(eventType, data, onEvent);
              } catch (e) {
                console.error('[SSE] Error parsing event data:', e, eventData);
              }
              eventType = 'message';
              eventData = '';
            }
          }
        }
      }
    } catch (error) {
      console.error('[SSE] Connection error:', error);
      this.isConnecting = false;
      this.isConnected = false;
      this.reader = null;
      this.handleDisconnect();
    }
  }

  /**
   * Handle incoming SSE event
   */
  handleEvent(eventType, data, onEvent) {
    console.log(`[SSE] Received event: ${eventType}`, data);
    
    if (onEvent && typeof onEvent === 'function') {
      onEvent(eventType, data);
    }

    // Also notify registered listeners
    const listeners = this.listeners.get(eventType) || [];
    listeners.forEach(listener => {
      try {
        listener(data);
      } catch (error) {
        console.error(`[SSE] Error in listener for ${eventType}:`, error);
      }
    });
  }

  /**
   * Notify listeners about connection status (for UI: reconnecting / reconnect_failed / connected).
   */
  _emitConnectionStatus(status, data = {}) {
    const payload = { status, ...data };
    const listeners = this.listeners.get('connection_status') || [];
    listeners.forEach(cb => {
      try {
        cb(payload);
      } catch (e) {
        console.error('[SSE] connection_status listener error:', e);
      }
    });
  }

  /**
   * Handle disconnection and attempt reconnect with exponential backoff (capped).
   * Uses currentOnEvent so reconnects always use the latest callback.
   */
  handleDisconnect() {
    if (typeof window === 'undefined') return;

    if (this.reconnectTimeoutId != null) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this._emitConnectionStatus('reconnect_failed');
      console.warn('[SSE] Max reconnection attempts reached - will retry on next visibility/focus');
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(
      this.reconnectDelay * this.reconnectAttempts,
      MAX_RECONNECT_DELAY_MS
    );
    this._emitConnectionStatus('reconnecting', {
      attempt: this.reconnectAttempts,
      maxAttempts: this.maxReconnectAttempts,
    });
    console.log(`[SSE] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    const onEvent = this.currentOnEvent;
    this.reconnectTimeoutId = setTimeout(() => {
      this.reconnectTimeoutId = null;
      this.connect(onEvent);
    }, delay);
  }

  /**
   * When user returns to the tab (e.g. after a day), try to reconnect if we're not connected.
   */
  startVisibilityReconnect() {
    if (typeof document === 'undefined' || this.visibilityHandler != null) return;

    this.visibilityHandler = () => {
      if (document.visibilityState !== 'visible') return;
      if (this.isConnected || this.isConnecting) return;
      const token = this.getAuthToken();
      if (!token) return;
      console.log('[SSE] Tab visible again - reconnecting...');
      this.reconnectAttempts = 0;
      this.connect(this.currentOnEvent);
    };

    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  stopVisibilityReconnect() {
    if (this.visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
  }

  /**
   * Handle authentication error (DEPRECATED - not used anymore)
   * 
   * SSE больше не удаляет токены при ошибках - это работа API сервиса.
   * SSE просто переподключается и использует обновленный JWT.
   */
  handleAuthError() {
    console.warn('[SSE] handleAuthError is deprecated - SSE should not manage tokens');
    // Не удаляем токены - это вызовет неожиданную потерю сессии
    // Просто закрываем соединение
    this.disconnect();
  }

  /**
   * Disconnect from SSE stream and cancel any scheduled reconnect.
   */
  disconnect() {
    if (this.reconnectTimeoutId != null) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }
    this.stopVisibilityReconnect();
    if (this.reader) {
      this.reader.cancel();
      this.reader = null;
    }
    this.isConnecting = false;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    console.log('[SSE] Disconnected');
  }

  /**
   * Add event listener
   */
  on(eventType, callback) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, []);
    }
    this.listeners.get(eventType).push(callback);
  }

  /**
   * Remove event listener
   */
  off(eventType, callback) {
    const listeners = this.listeners.get(eventType);
    if (listeners) {
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }
}

// Export singleton instance
export const sseService = new SSEService();

// Export convenience functions for backward compatibility
export const connectSSE = (onEvent) => sseService.connect(onEvent);
export const disconnectSSE = () => sseService.disconnect();

